/**
 * Entry Screen — Web Version
 *
 * Choice screen + B2C two-step email wizard:
 * - "choice":     HolDeinePlakette logo + Geschäftskunden / Privatkunden buttons
 * - "b2c-email":  Email input + Weiter button (clean, minimal)
 * - "b2c-sent":   Animated confirmation — "Bestätigungslink per E-Mail zugesendet"
 *
 * Geschäftskunden navigates to the existing FleetHubLogin screen.
 *
 * @module screens/EntryScreen
 */

import { FC, useEffect, useRef, useState } from "react"
import {
  ActivityIndicator,
  Animated,
  Image,
  // eslint-disable-next-line no-restricted-imports
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native"
import { useNavigation } from "@react-navigation/native"
import type { NativeStackNavigationProp } from "@react-navigation/native-stack"
import { onAuthStateChanged } from "firebase/auth"
import { observer } from "mobx-react-lite"

import { Button } from "@/components/Button"
import { Footer } from "@/components/Footer"
import { Icon } from "@/components/Icon"
import { Text } from "@/components/Text"
import { TrackingMapView } from "@/components/TrackingMapView.web"
import { useStores } from "@/models"
import type { AppStackParamList } from "@/navigators/AppNavigator"
import { auth } from "@/services/firebase/auth"
import { isBlockedFromFleetHub } from "@/services/firebase/b2bAccess"
import { resendTrackingLink } from "@/services/firebase/functions"
import { breakpoints, colors } from "@/theme"

import * as S from "./EntryScreen.styles"

type ViewState = "choice" | "b2c-email" | "b2c-sent"

const HOVER_STYLES = `
  [data-choice-btn] {
    transition: transform 0.2s ease, box-shadow 0.2s ease !important;
  }
  [data-choice-btn="business"]:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 20px rgba(255, 56, 47, 0.35) !important;
  }
  [data-choice-btn="private"]:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 20px rgba(17, 30, 44, 0.2) !important;
  }
  [data-entry-btn="primary"] > *:hover {
    background-color: ${colors.surface} !important;
    border-color: ${colors.text} !important;
    border-width: 2px !important;
    transform: scale(1.02) !important;
    box-shadow: 0 4px 16px rgba(17, 30, 44, 0.15) !important;
  }
  [data-entry-btn="primary"] > *:hover * {
    color: ${colors.text} !important;
  }
  [data-entry-btn="secondary"] > *:hover {
    background-color: ${colors.surface} !important;
    border-color: ${colors.text} !important;
    border-width: 2px !important;
    transform: scale(1.02) !important;
    box-shadow: 0 4px 16px rgba(17, 30, 44, 0.15) !important;
  }
  [data-entry-btn="secondary"] > *:hover * {
    color: ${colors.text} !important;
  }
`

export const EntryScreen: FC = observer(function EntryScreen() {
  const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>()
  const { width } = useWindowDimensions()
  const isMobile = width < breakpoints.mobile

  const [view, setView] = useState<ViewState>("choice")
  const viewRef = useRef<ViewState>("choice")
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  // Animation ref for success checkmark
  const checkScale = useRef(new Animated.Value(0)).current
  const checkOpacity = useRef(new Animated.Value(0)).current

  const { orderTrackingStore } = useStores()

  useEffect(() => {
    const id = "entry-screen-hover-styles"
    if (!document.getElementById(id)) {
      const style = document.createElement("style")
      style.id = id
      style.textContent = HOVER_STYLES
      document.head.appendChild(style)
    }
    return () => {
      document.getElementById(id)?.remove()
    }
  }, [])

  useEffect(() => {
    orderTrackingStore.loadAllLocations()
  }, [])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) return
      // PLA-534: the root route auto-forwards any signed-in user into FleetHub,
      // which is how a poisoned internal session loops back into the 403
      // dashboard on every visit. A blocked user simply stays on the choice
      // screen; signing them out here would be surprising, and the login screen
      // already does it with an explanation.
      void (async () => {
        if (await isBlockedFromFleetHub(user)) return
        navigation.replace("FleetHub")
      })()
    })
    return () => unsubscribe()
  }, [navigation])

  const runSuccessAnimation = () => {
    checkScale.setValue(0)
    checkOpacity.setValue(0)

    Animated.parallel([
      Animated.spring(checkScale, {
        toValue: 1,
        friction: 4,
        tension: 80,
        useNativeDriver: true,
      }),
      Animated.timing(checkOpacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start()
  }

  const changeView = (v: ViewState) => {
    viewRef.current = v
    setView(v)
  }

  const handleSubmitEmail = async () => {
    if (!email || !email.includes("@")) {
      setError("Bitte gib eine gültige E-Mail-Adresse ein")
      return
    }
    setLoading(true)
    setError("")
    try {
      await resendTrackingLink(email.toLowerCase().trim())
      // Only transition if user hasn't navigated away during the request
      if (viewRef.current === "b2c-email") {
        changeView("b2c-sent")
        runSuccessAnimation()
      }
    } catch {
      if (viewRef.current === "b2c-email") {
        setError("Anfrage fehlgeschlagen. Bitte versuche es erneut.")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoBack = () => {
    setError("")
    setEmail("")
    changeView("choice")
  }

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter" && !loading && email.includes("@")) {
      handleSubmitEmail()
    }
  }

  // ── View Renderers ────────────────────────────────────────────────────────────

  const renderChoiceView = () => (
    <>
      <View style={S.$logoContainer}>
        <Image
          source={require("@assets/images/HolDeinePlakette_Logo_Red.png")}
          style={S.$logo}
          resizeMode="contain"
        />
      </View>
      <View style={S.$divider} />

      <TouchableOpacity
        // @ts-expect-error - dataSet is React Native Web only
        dataSet={{ choiceBtn: "business" }}
        style={S.$choiceButton}
        onPress={() => navigation.navigate("FleetHubLogin")}
        activeOpacity={0.8}
      >
        <View style={S.$choiceButtonInner}>
          <View style={S.$choiceButtonTextWrap}>
            <Text style={S.$choiceButtonTitle}>Geschäftskunden</Text>
            <Text style={S.$choiceButtonSubtitle}>FleetHub Login</Text>
          </View>
          <Icon icon="caretRight" size={20} color={colors.palette.neutral100} />
        </View>
      </TouchableOpacity>

      <TouchableOpacity
        // @ts-expect-error - dataSet is React Native Web only
        dataSet={{ choiceBtn: "private" }}
        style={[S.$choiceButton, S.$choiceButtonOutline]}
        onPress={() => changeView("b2c-email")}
        activeOpacity={0.8}
      >
        <View style={S.$choiceButtonInner}>
          <View style={S.$choiceButtonTextWrap}>
            <Text style={[S.$choiceButtonTitle, S.$choiceButtonTitleDark]}>Privatkunden</Text>
            <Text style={[S.$choiceButtonSubtitle, S.$choiceButtonSubtitleDark]}>
              Auftrag verwalten & Livetracking
            </Text>
          </View>
          <Icon icon="caretRight" size={20} color={colors.background} />
        </View>
      </TouchableOpacity>
    </>
  )

  const renderB2cEmailView = () => (
    <>
      <TouchableOpacity style={S.$backRow} onPress={handleGoBack} activeOpacity={0.7}>
        <Icon icon="caretLeft" size={18} color={colors.textDim} />
        <Text style={S.$backLabel}>Zurück</Text>
      </TouchableOpacity>

      <Text style={S.$formTitle}>Auftrag verwalten & Livetracking</Text>

      <TextInput
        style={S.$input}
        placeholder="E-Mail"
        placeholderTextColor={colors.textDim}
        value={email}
        onChangeText={setEmail}
        autoCapitalize="none"
        keyboardType="email-address"
        autoComplete="email"
        editable={!loading}
        onKeyPress={handleKeyPress}
      />

      {error ? (
        <View style={S.$errorContainer}>
          <Text style={S.$errorText}>{error}</Text>
        </View>
      ) : null}

      {/* @ts-expect-error - dataSet is React Native Web only */}
      <View dataSet={{ entryBtn: "primary" }}>
        <Button
          text={loading ? "" : "Weiter"}
          onPress={handleSubmitEmail}
          disabled={loading || !email.includes("@")}
          preset="filled"
          style={S.$primaryButton}
          textStyle={S.$primaryButtonText}
        >
          {loading && <ActivityIndicator size="small" color={colors.palette.neutral100} />}
        </Button>
      </View>

      <Text style={S.$bookingLink}>
        Noch keinen Auftrag?{" "}
        <Text
          style={S.$bookingLinkHighlight}
          onPress={() => window.open("https://www.holdeineplakette.de", "_blank")}
        >
          Jetzt Termin buchen
        </Text>
      </Text>
    </>
  )

  const renderB2cSentView = () => (
    <>
      <TouchableOpacity style={S.$backRow} onPress={handleGoBack} activeOpacity={0.7}>
        <Icon icon="caretLeft" size={18} color={colors.textDim} />
        <Text style={S.$backLabel}>Zurück</Text>
      </TouchableOpacity>

      <View style={S.$successContainer}>
        <Animated.View
          style={[
            S.$successCheckCircle,
            { transform: [{ scale: checkScale }], opacity: checkOpacity },
          ]}
        >
          <Text style={S.$successCheckMark}>✓</Text>
        </Animated.View>

        <Text style={S.$successTitle}>Schau in dein Postfach</Text>
        <Text style={S.$successText}>
          Wir haben dir deinen Verwaltungs- & Tracking-Link per E-Mail zugesendet. Die E-Mail
          erreicht dich innerhalb der nächsten 2 Minuten.
        </Text>
      </View>

      {/* @ts-expect-error - dataSet is React Native Web only */}
      <View dataSet={{ entryBtn: "secondary" }}>
        <Button
          text="Zurück zur Startseite"
          onPress={handleGoBack}
          preset="default"
          style={S.$secondaryButton}
          textStyle={S.$secondaryButtonText}
        />
      </View>
    </>
  )

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <View style={S.$container}>
      <View style={S.$mapContainer}>
        <TrackingMapView
          showAllLocations={true}
          allLocations={orderTrackingStore.allLocations.slice()}
          driverLocation={null}
          customerLocation={null}
          pickupLocation={null}
          showRoute={false}
        />
      </View>

      <View style={[S.$contentContainer, isMobile && S.$contentContainerMobile]}>
        <View style={S.$form}>
          {view === "choice" && renderChoiceView()}
          {view === "b2c-email" && renderB2cEmailView()}
          {view === "b2c-sent" && renderB2cSentView()}
        </View>
      </View>

      <View style={S.$footer}>
        <Footer />
      </View>
    </View>
  )
})
